import { testPermission, type Principal } from './permissions.js';
import { NodeTree, attrBytes, foldName, nodeBytes, permBytes, type TreeNode } from './tree.js';
import type { NodeId, NodeSnapshot, Op, OpBatch, Permission, SequencedBatch } from './types.js';

export type SubmitResult =
  | { ok: true; batch: SequencedBatch }
  | { ok: false; error: string; opIndex: number };

/** The error of a batch refused by `DocumentLimits`. */
export const TOO_LARGE = 'document too large';

/** Ceiling of a document: its snapshot as JSON (`NodeTree.bytes`) and its node count. */
export interface DocumentLimits {
  bytes: number;
  nodes: number;
}

export interface DocumentOptions {
  /** Logins that bypass permission checks (the database owner, e.g. the room's GMs). */
  isOwner?: (who: Principal) => boolean;
  /** How many sequenced batches to keep for catch-up after a short disconnection. */
  logLimit?: number;
  /** How many bytes of batches (as JSON) the catch-up log may hold; past that, catch-up goes by snapshot. */
  logBytes?: number;
  /**
   * A batch that would leave the document above a limit, and bigger than it was, is refused
   * (`TOO_LARGE`). A document already above one can still shrink.
   */
  limits?: DocumentLimits;
}

/**
 * Server-side authoritative copy of one node database. Validates and orders
 * batches; a batch is applied entirely or not at all.
 */
export class NdbDocument {
  readonly tree: NodeTree;
  private seq: number;
  private readonly log: { batch: SequencedBatch; bytes: number }[] = [];
  private logTotal = 0;
  private readonly isOwner: (who: Principal) => boolean;
  private readonly logLimit: number;
  private readonly logBytes: number;
  private readonly limits: DocumentLimits | null;

  constructor(snapshot?: NodeSnapshot, seq = 0, opts: DocumentOptions = {}) {
    this.tree = snapshot ? NodeTree.fromSnapshot(snapshot) : new NodeTree();
    this.seq = seq;
    this.isOwner = opts.isOwner ?? (() => false);
    this.logLimit = opts.logLimit ?? 2000;
    this.logBytes = opts.logBytes ?? Infinity;
    this.limits = opts.limits ?? null;
  }

  get currentSeq(): number {
    return this.seq;
  }

  submit(batch: OpBatch, who: Principal): SubmitResult {
    const err = this.validate(batch.ops, who) ?? this.overLimits(batch.ops);
    if (err) return { ok: false, ...err };
    for (const op of batch.ops) this.tree.apply(op);
    const sequenced: SequencedBatch = { ...batch, seq: ++this.seq };
    this.remember(sequenced);
    return { ok: true, batch: sequenced };
  }

  /**
   * Re-applies a batch that was already accepted (loading from storage): no
   * permission checks, keeps the stored sequence number.
   */
  replay(batch: SequencedBatch): void {
    for (const op of batch.ops) this.tree.apply(op);
    this.seq = batch.seq;
    this.remember(batch);
  }

  /** Keeps the batch for catch-up, dropping the oldest past `logLimit` batches or `logBytes`. */
  private remember(batch: SequencedBatch): void {
    const bytes = JSON.stringify(batch).length;
    this.log.push({ batch, bytes });
    this.logTotal += bytes;
    while (this.log.length > 0 && (this.log.length > this.logLimit || this.logTotal > this.logBytes)) this.logTotal -= this.log.shift()!.bytes;
  }

  /** Batches after `seq`, or null when the log no longer reaches back that far. */
  since(seq: number): SequencedBatch[] | null {
    if (seq === this.seq) return [];
    const first = this.log[0]?.batch;
    if (!first || first.seq > seq + 1) return null;
    return this.log.filter((e) => e.batch.seq > seq).map((e) => e.batch);
  }

  can(who: Principal, node: NodeId, permission: Permission): boolean {
    return this.isOwner(who) || testPermission(this.tree, node, who, permission);
  }

  /** Is any node hidden from someone (an explicit `read` deny)? Catch-up must then go by snapshot. */
  hasReadRestrictions(): boolean {
    const walk = (n: TreeNode): boolean => n.perms.some((p) => p.permission === 'read' && p.allowance === 'deny') || n.children.some(walk);
    return walk(this.tree.root);
  }

  /**
   * How a just-accepted batch reaches `who`, given which of its nodes `who`
   * could read before it applied (`readableBefore`, from `readableTargets`):
   * - 'batch': every node involved is readable before and after;
   * - 'skip': none is (nothing visible changed for them);
   * - 'snapshot': visibility changed (permissions, or a node crossing the line).
   */
  deliveryFor(batch: OpBatch, who: Principal, readableBefore: boolean[]): 'batch' | 'skip' | 'snapshot' {
    if (batch.ops.some((op) => op.t === 'perm' || op.t === 'resetPerms')) return this.isOwner(who) ? 'batch' : 'snapshot';
    const after: boolean[] = [];
    for (const op of batch.ops) {
      const ids = op.t === 'add' ? [op.parent, op.id] : op.t === 'del' ? [] : [op.node];
      for (const id of ids) if (this.tree.has(id)) after.push(this.can(who, id, 'read'));
    }
    const all = [...readableBefore, ...after];
    if (all.every(Boolean)) return 'batch';
    if (!all.some(Boolean)) return 'skip';
    return 'snapshot';
  }

  /** Which nodes of the batch `who` can read now (call before submitting it). */
  readableTargets(batch: OpBatch, who: Principal): boolean[] {
    const out: boolean[] = [];
    for (const op of batch.ops) {
      const id = op.t === 'add' ? op.parent : op.node;
      if (this.tree.has(id)) out.push(this.can(who, id, 'read'));
    }
    return out;
  }

  /** The tree as `who` may see it: nodes without effective `read` are pruned (root always kept). */
  snapshotFor(who: Principal): NodeSnapshot {
    const full = this.tree.snapshot();
    if (this.isOwner(who)) return full;
    const prune = (s: NodeSnapshot): NodeSnapshot => ({
      ...s,
      children: s.children.filter((c) => this.can(who, c.id, 'read')).map(prune),
    });
    return prune(full);
  }

  /** Checks the whole batch before touching the tree (see `validateOps`). */
  private validate(ops: Op[], who: Principal): { error: string; opIndex: number } | null {
    return validateOps(this.tree, ops, (id, permission) => this.can(who, id, permission));
  }

  /**
   * Would the (valid) batch leave the document above its limits, and bigger than it is? Far from
   * them, what the batch can add at most answers it; near them, the batch runs on a copy.
   */
  private overLimits(ops: Op[]): { error: string; opIndex: number } | null {
    const limits = this.limits;
    if (!limits) return null;
    const most = growthBound(ops);
    if (this.tree.bytes + most.bytes <= limits.bytes && this.tree.nodeCount + most.nodes <= limits.nodes) return null;
    const trial = this.tree.clone();
    for (const op of ops) trial.apply(op);
    const over =
      (trial.bytes > limits.bytes && trial.bytes > this.tree.bytes) || (trial.nodeCount > limits.nodes && trial.nodeCount > this.tree.nodeCount);
    return over ? { error: TOO_LARGE, opIndex: ops.length - 1 } : null;
  }
}

/** The most a batch can add to a tree (`NodeTree.bytes` and nodes): removals count as nothing. */
function growthBound(ops: Op[]): { bytes: number; nodes: number } {
  let bytes = 0;
  let nodes = 0;
  for (const op of ops) {
    if (op.t === 'set' && op.value !== null) bytes += attrBytes(op.name, op.value);
    else if (op.t === 'add') {
      bytes += nodeBytes(op.id, op.name);
      nodes += 1;
    } else if (op.t === 'perm' && op.allowance !== null) bytes += permBytes({ id: op.id, permission: op.permission, allowance: op.allowance });
  }
  return { bytes, nodes };
}

/**
 * Checks a batch against a tree before applying it, simulating nodes created and
 * deleted earlier in the same batch so the batch applies atomically. `can` says
 * whether the author holds a permission on an existing node. Used by the server
 * (authoritative) and by replicas (to refuse up front what the server would).
 */
export function validateOps(tree: NodeTree, ops: Op[], can: (id: NodeId, permission: Permission) => boolean): { error: string; opIndex: number } | null {
  const created = new Map<NodeId, NodeId>(); // id -> parent, for nodes added in this batch
  const createdNames = new Map<NodeId, string>();
  const deleted = new Set<NodeId>();
  const exists = (id: NodeId): boolean =>
    !deleted.has(id) && (created.has(id) ? exists(created.get(id)!) : tree.has(id));
  // A node created earlier in this batch was already authorized by its `add`.
  const need = (id: NodeId, p: Permission) => created.has(id) || can(id, p);

  for (const [i, op] of ops.entries()) {
    const fail = (error: string) => ({ error, opIndex: i });
    switch (op.t) {
      case 'set':
        if (!exists(op.node)) return fail(`unknown node ${op.node}`);
        if (!need(op.node, 'write')) return fail('permission denied: write');
        break;
      case 'clear':
      case 'rmChildren': {
        if (!exists(op.node)) return fail(`unknown node ${op.node}`);
        if (!need(op.node, 'write')) return fail('permission denied: write');
        // tirar filhos é apagá-los: vale a mesma regra do `del` para cada um (senão quem só
        // escreve no nó apagaria um filho protegido, como a visão de um token na cena)
        const folded = op.t === 'rmChildren' ? foldName(op.name) : null;
        const removed = [
          ...(tree.get(op.node)?.children ?? []).filter((c) => !deleted.has(c.id)).map((c) => ({ id: c.id, name: c.name })),
          ...[...created].filter(([, parent]) => parent === op.node).map(([id]) => ({ id, name: createdNames.get(id) ?? '' })),
        ].filter((c) => folded === null || foldName(c.name) === folded);
        for (const child of removed) {
          if (!need(child.id, 'delete')) return fail('permission denied: delete');
          deleted.add(child.id);
          for (const n of tree.has(child.id) ? subtreeIds(tree, child.id) : []) deleted.add(n);
        }
        break;
      }
      case 'add':
        if (!exists(op.parent)) return fail(`unknown node ${op.parent}`);
        if (tree.has(op.id) || created.has(op.id)) return fail(`node ${op.id} already exists`);
        if (!need(op.parent, 'createChild')) return fail('permission denied: createChild');
        if (op.name.length === 0 || op.name.length > 256) return fail('invalid node name');
        created.set(op.id, op.parent);
        createdNames.set(op.id, op.name);
        break;
      case 'del': {
        if (!exists(op.node)) return fail(`unknown node ${op.node}`);
        if (op.node === tree.root.id) return fail('cannot delete the root');
        if (!need(op.node, 'delete')) return fail('permission denied: delete');
        deleted.add(op.node);
        for (const n of subtreeIds(tree, op.node)) deleted.add(n);
        break;
      }
      case 'move': {
        if (!exists(op.node)) return fail(`unknown node ${op.node}`);
        const parent = created.get(op.node) ?? tree.get(op.node)?.parent?.id;
        if (parent === undefined) return fail('cannot move the root');
        // reordenar os filhos é mexer no pai
        if (!need(parent, 'write')) return fail('permission denied: write');
        if (!Number.isInteger(op.index) || op.index < 0) return fail('invalid index');
        break;
      }
      case 'perm':
      case 'resetPerms':
        if (!exists(op.node)) return fail(`unknown node ${op.node}`);
        if (!need(op.node, 'writePermissions')) return fail('permission denied: writePermissions');
        break;
    }
  }
  return null;
}

function subtreeIds(tree: NodeTree, id: NodeId): NodeId[] {
  const out: NodeId[] = [];
  const walk = (n: TreeNode) => {
    out.push(n.id);
    n.children.forEach(walk);
  };
  const n = tree.get(id);
  if (n) walk(n);
  return out;
}
